import React, { useEffect, useState } from "react";
import moment from "moment";
import { Link, useParams } from "react-router-dom";
import {
  Card,
  Button,
  Modal,
  Input,
  List,
  Popconfirm,
  message,
  Space,
  Typography,
  Empty,
  DatePicker,
  Tag,
  Select,
  Row,
  Col,
} from "antd";
import {
  ArrowLeftOutlined,
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
} from "@ant-design/icons";
import adminApi from "./adminApi";
import ItemPickerModal from "./ItemPickerModal";
import {
  eventImagePresets,
  getEventFallbackImage,
  getEventImageUrl,
  getPresetByImageUrl,
} from "../eventImages";

const { Title, Text } = Typography;
const { Option } = Select;
const CUSTOM_IMAGE_VALUE = "__custom__";

const errorMessageFrom = (error, fallback) =>
  (error.response && error.response.data && error.response.data.message) ||
  fallback;

const eventStatus = (event) => {
  if (!event.ticketTypes || event.ticketTypes.length === 0) {
    return "coming-soon";
  }
  const now = new Date();
  if (event.startDate && now < new Date(event.startDate)) {
    return "coming-soon";
  }
  if (event.endDate && now > new Date(event.endDate)) {
    return "closed";
  }
  return "on-sale";
};

const statusColor = {
  "on-sale": "success",
  "coming-soon": "default",
  closed: "warning",
};

const statusLabel = {
  "on-sale": "On Sale",
  "coming-soon": "Coming Soon",
  closed: "Closed",
};

const formatSaleWindow = (event) => {
  if (!event.startDate && !event.endDate) return "Open";
  const start = event.startDate
    ? new Date(event.startDate).toLocaleString()
    : "Now";
  const end = event.endDate
    ? new Date(event.endDate).toLocaleString()
    : "No end";
  return `${start} - ${end}`;
};

const checkoutStatusText = (event) => {
  const status = eventStatus(event);
  if (status === "on-sale") return "Checkout is currently open.";
  if (status === "closed") return "Checkout is currently closed.";
  return "Checkout is not open yet.";
};

export default function EventAdminPage() {
  const { eventKey } = useParams();
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);

  const [detailsEditorEvent, setDetailsEditorEvent] = useState(null);
  const [detailsNameDraft, setDetailsNameDraft] = useState("");
  const [detailsDescriptionDraft, setDetailsDescriptionDraft] = useState("");
  const [savingDetails, setSavingDetails] = useState(false);

  const [workbooks, setWorkbooks] = useState([]);
  const [workbooksLoading, setWorkbooksLoading] = useState(false);
  const [workbooksLoaded, setWorkbooksLoaded] = useState(false);
  const [workbookPickerEvent, setWorkbookPickerEvent] = useState(null);

  const [qbItems, setQbItems] = useState([]);
  const [qbItemsLoading, setQbItemsLoading] = useState(false);
  const [qbItemsLoaded, setQbItemsLoaded] = useState(false);
  const [qbPickerOpen, setQbPickerOpen] = useState(false);

  const [ticketTypeEditor, setTicketTypeEditor] = useState(null);
  const [savingTicketType, setSavingTicketType] = useState(false);

  const [imageEditorEvent, setImageEditorEvent] = useState(null);
  const [imageMode, setImageMode] = useState("");
  const [imageUrlDraft, setImageUrlDraft] = useState("");
  const [savingImage, setSavingImage] = useState(false);

  const [scheduleEditorEvent, setScheduleEditorEvent] = useState(null);
  const [scheduleStart, setScheduleStart] = useState(null);
  const [scheduleEnd, setScheduleEnd] = useState(null);
  const [savingSchedule, setSavingSchedule] = useState(false);

  const loadEvents = async () => {
    setLoadingEvents(true);
    try {
      const { data } = await adminApi.get("/api/admin/events");
      setEvents(data);
    } catch (error) {
      message.error("Failed to load events.");
    }
    setLoadingEvents(false);
  };

  useEffect(() => {
    loadEvents();
  }, []);

  const event = events.find((item) => item.key === eventKey);

  const openDetailsEditor = (event) => {
    setDetailsEditorEvent(event);
    setDetailsNameDraft(event.name || "");
    setDetailsDescriptionDraft(event.description || "");
  };

  const closeDetailsEditor = () => {
    setDetailsEditorEvent(null);
    setDetailsNameDraft("");
    setDetailsDescriptionDraft("");
  };

  const saveDetails = async () => {
    if (!detailsNameDraft.trim()) return;
    setSavingDetails(true);
    try {
      const { data } = await adminApi.put(
        `/api/admin/events/${detailsEditorEvent.key}`,
        {
          name: detailsNameDraft.trim(),
          description: detailsDescriptionDraft.trim(),
        }
      );
      setEvents((prev) => prev.map((e) => (e.key === data.key ? data : e)));
      message.success(`${data.name}: details updated.`);
      closeDetailsEditor();
    } catch (error) {
      message.error(errorMessageFrom(error, "Failed to update event details."));
    }
    setSavingDetails(false);
  };

  const openWorkbookPicker = async (event) => {
    setWorkbookPickerEvent(event);
    if (workbooksLoaded) return;
    setWorkbooksLoading(true);
    try {
      const { data } = await adminApi.get("/api/admin/microsoft/workbooks");
      setWorkbooks(data.map((wb) => ({ id: wb.id, name: wb.name })));
      setWorkbooksLoaded(true);
    } catch (error) {
      message.error(errorMessageFrom(error, "Failed to load Excel workbooks."));
    }
    setWorkbooksLoading(false);
  };

  const selectWorkbook = async (workbook) => {
    const event = workbookPickerEvent;
    setWorkbookPickerEvent(null);
    try {
      const { data } = await adminApi.put(`/api/admin/events/${event.key}`, {
        workbookId: workbook.id,
        workbookName: workbook.name,
      });
      setEvents((prev) => prev.map((e) => (e.key === data.key ? data : e)));
      message.success(
        `${event.name}: Excel workbook connected (${data.workbookColumnCount} columns).`
      );
    } catch (error) {
      message.error(errorMessageFrom(error, "Failed to update event."));
    }
  };

  const loadQbItemsIfNeeded = async () => {
    if (qbItemsLoaded) return;
    setQbItemsLoading(true);
    try {
      const { data } = await adminApi.get("/api/admin/quickbooks/items");
      setQbItems(
        data.map((item) => ({
          id: item.id,
          name: item.name,
          sublabel: `#${item.id} · $${item.unitPrice}`,
        }))
      );
      setQbItemsLoaded(true);
    } catch (error) {
      message.error(errorMessageFrom(error, "Failed to load QuickBooks items."));
    }
    setQbItemsLoading(false);
  };

  const openAddTicketType = (event) => {
    setTicketTypeEditor({ event, ticketType: null, name: "", qbItem: null });
    loadQbItemsIfNeeded();
  };

  const openEditTicketType = (event, ticketType) => {
    setTicketTypeEditor({
      event,
      ticketType,
      name: ticketType.name,
      qbItem: { id: ticketType.qbItemId, name: ticketType.qbItemName },
    });
    loadQbItemsIfNeeded();
  };

  const closeTicketTypeEditor = () => setTicketTypeEditor(null);

  const selectQbItemForTicketType = (item) => {
    setTicketTypeEditor((prev) => ({ ...prev, qbItem: item }));
    setQbPickerOpen(false);
  };

  const saveTicketType = async () => {
    const { event, ticketType, name, qbItem } = ticketTypeEditor;
    if (!name.trim() || !qbItem) return;
    setSavingTicketType(true);
    try {
      const body = { name: name.trim(), qbItemId: qbItem.id };
      let data;
      if (ticketType) {
        ({ data } = await adminApi.put(
          `/api/admin/events/${event.key}/ticket-types/${ticketType._id}`,
          body
        ));
      } else {
        ({ data } = await adminApi.post(
          `/api/admin/events/${event.key}/ticket-types`,
          body
        ));
      }
      setEvents((prev) => prev.map((e) => (e.key === data.key ? data : e)));
      message.success(`${event.name}: ticket type saved.`);
      closeTicketTypeEditor();
    } catch (error) {
      message.error(errorMessageFrom(error, "Failed to save ticket type."));
    }
    setSavingTicketType(false);
  };

  const openImageEditor = (event) => {
    const preset = getPresetByImageUrl(event.imageUrl || "");
    setImageEditorEvent(event);
    setImageMode(event.imageUrl ? preset?.value || CUSTOM_IMAGE_VALUE : "");
    setImageUrlDraft(event.imageUrl || "");
  };

  const closeImageEditor = () => {
    setImageEditorEvent(null);
    setImageMode("");
    setImageUrlDraft("");
  };

  const selectImagePreset = (value) => {
    setImageMode(value || "");
    if (!value) {
      setImageUrlDraft("");
      return;
    }
    if (value === CUSTOM_IMAGE_VALUE) {
      if (!imageUrlDraft || getPresetByImageUrl(imageUrlDraft)) {
        setImageUrlDraft("");
      }
      return;
    }
    setImageUrlDraft(value);
  };

  const saveImage = async () => {
    setSavingImage(true);
    try {
      const { data } = await adminApi.put(
        `/api/admin/events/${imageEditorEvent.key}`,
        { imageUrl: imageUrlDraft.trim() }
      );
      setEvents((prev) => prev.map((e) => (e.key === data.key ? data : e)));
      message.success(`${imageEditorEvent.name}: image updated.`);
      closeImageEditor();
    } catch (error) {
      message.error(errorMessageFrom(error, "Failed to update image."));
    }
    setSavingImage(false);
  };

  const openScheduleEditor = (event) => {
    setScheduleEditorEvent(event);
    setScheduleStart(event.startDate ? moment(event.startDate) : null);
    setScheduleEnd(event.endDate ? moment(event.endDate) : null);
  };

  const closeScheduleEditor = () => {
    setScheduleEditorEvent(null);
    setScheduleStart(null);
    setScheduleEnd(null);
  };

  const saveSchedule = async () => {
    if (scheduleStart && scheduleEnd && !scheduleEnd.isAfter(scheduleStart)) {
      message.error("End time must be after the start time.");
      return;
    }
    setSavingSchedule(true);
    try {
      const { data } = await adminApi.put(
        `/api/admin/events/${scheduleEditorEvent.key}`,
        {
          startDate: scheduleStart ? scheduleStart.toISOString() : null,
          endDate: scheduleEnd ? scheduleEnd.toISOString() : null,
        }
      );
      setEvents((prev) => prev.map((e) => (e.key === data.key ? data : e)));
      message.success(`${scheduleEditorEvent.name}: schedule updated.`);
      closeScheduleEditor();
    } catch (error) {
      message.error(errorMessageFrom(error, "Failed to update schedule."));
    }
    setSavingSchedule(false);
  };

  const removeTicketType = async (event, ticketType) => {
    try {
      const { data } = await adminApi.delete(
        `/api/admin/events/${event.key}/ticket-types/${ticketType._id}`
      );
      setEvents((prev) => prev.map((e) => (e.key === data.key ? data : e)));
      message.success(`Removed "${ticketType.name}" from ${event.name}.`);
    } catch (error) {
      message.error(errorMessageFrom(error, "Failed to remove ticket type."));
    }
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "0.5rem",
        }}
      >
        <Space>
          <Link to="/admin/events">
            <Button icon={<ArrowLeftOutlined />}>Events</Button>
          </Link>
          <Title level={3} style={{ margin: 0 }}>
            {event ? event.name : "Event"}
          </Title>
        </Space>
      </div>

      <div style={{ marginTop: "1rem" }}>
        {loadingEvents && !event ? (
          <Card loading />
        ) : !event ? (
          <Card>
            <Empty description="Event not found" />
          </Card>
        ) : event ? (
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            <Card
              title="Event Details"
              extra={<Button onClick={() => openDetailsEditor(event)}>Edit</Button>}
            >
              <Space direction="vertical" size={8}>
                <Space wrap>
                  <Text strong>{event.name}</Text>
                  <Tag color={statusColor[eventStatus(event)]}>
                    {statusLabel[eventStatus(event)]}
                  </Tag>
                </Space>
                <Text type={event.description ? undefined : "secondary"}>
                  {event.description || "No description set."}
                </Text>
              </Space>
            </Card>

            <Row gutter={[16, 16]}>
              <Col xs={24} lg={12}>
                <Card
                  title="Event Image"
                  extra={
                    <Button onClick={() => openImageEditor(event)}>Edit</Button>
                  }
                >
                  <Space direction="vertical" size={8} style={{ width: "100%" }}>
                    <img
                      src={getEventImageUrl(event)}
                      alt={event.name}
                      style={{
                        width: "100%",
                        aspectRatio: "16 / 9",
                        objectFit: "cover",
                        borderRadius: 4,
                      }}
                    />
                    {!event.imageUrl ? <Tag>Using fallback image</Tag> : null}
                  </Space>
                </Card>
              </Col>

              <Col xs={24} lg={12}>
                <Card
                  title="Schedule"
                  extra={
                    <Button onClick={() => openScheduleEditor(event)}>
                      Edit
                    </Button>
                  }
                >
                  <Space direction="vertical" size={8}>
                    <Text strong>{formatSaleWindow(event)}</Text>
                    <Text type="secondary">{checkoutStatusText(event)}</Text>
                  </Space>
                </Card>
              </Col>
            </Row>

            <Card
              title="Tickets"
              extra={
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => openAddTicketType(event)}
                >
                  Add Ticket Type
                </Button>
              }
            >
              <List
                dataSource={event.ticketTypes}
                locale={{
                  emptyText: <Empty description="No ticket types" />,
                }}
                renderItem={(ticketType) => (
                  <List.Item
                    actions={[
                      <Button
                        key="edit"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => openEditTicketType(event, ticketType)}
                      />,
                      <Popconfirm
                        key="remove"
                        title={`Remove "${ticketType.name}"?`}
                        onConfirm={() => removeTicketType(event, ticketType)}
                        okText="Remove"
                        okButtonProps={{ danger: true }}
                      >
                        <Button size="small" danger icon={<DeleteOutlined />} />
                      </Popconfirm>,
                    ]}
                  >
                    <List.Item.Meta
                      title={ticketType.name}
                      description={
                        <Text type="secondary">
                          {ticketType.qbItemName}
                          {ticketType.unitPrice != null
                            ? ` · $${ticketType.unitPrice}`
                            : ""}
                        </Text>
                      }
                    />
                  </List.Item>
                )}
              />
            </Card>

            <Card
              title="Integration"
              extra={
                <Button onClick={() => openWorkbookPicker(event)}>
                  Choose Workbook
                </Button>
              }
            >
              <Space direction="vertical" size={8}>
                <Text strong>
                  {event.workbookName ||
                    event.workbookId ||
                    "No workbook connected"}
                </Text>
                <Text type="secondary">
                  {event.workbookColumnCount
                    ? `${event.workbookColumnCount} columns found in Table1.`
                    : "Connect an Excel workbook with a Table1 sheet for attendee data."}
                </Text>
              </Space>
            </Card>
          </Space>
        ) : null}
      </div>

      <ItemPickerModal
        open={!!workbookPickerEvent}
        title={
          workbookPickerEvent
            ? `${workbookPickerEvent.name}: Choose Excel Workbook`
            : ""
        }
        loading={workbooksLoading}
        items={workbooks}
        onSelect={selectWorkbook}
        onCancel={() => setWorkbookPickerEvent(null)}
      />

      <Modal
        title={
          detailsEditorEvent ? `${detailsEditorEvent.name}: Event Details` : ""
        }
        visible={!!detailsEditorEvent}
        onCancel={closeDetailsEditor}
        onOk={saveDetails}
        confirmLoading={savingDetails}
        okButtonProps={{ disabled: !detailsNameDraft.trim() }}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <div>
            <Text>Event name</Text>
            <Input
              placeholder="Event name"
              value={detailsNameDraft}
              onChange={(e) => setDetailsNameDraft(e.target.value)}
              style={{ marginTop: "0.25rem" }}
              autoFocus
            />
          </div>
          <div>
            <Text>Description</Text>
            <Input.TextArea
              placeholder="Short description shown with this event"
              value={detailsDescriptionDraft}
              onChange={(e) => setDetailsDescriptionDraft(e.target.value)}
              autoSize={{ minRows: 3, maxRows: 6 }}
              style={{ marginTop: "0.25rem" }}
            />
          </div>
        </Space>
      </Modal>

      <Modal
        title={
          ticketTypeEditor && ticketTypeEditor.ticketType
            ? "Edit Ticket Type"
            : "Add Ticket Type"
        }
        visible={!!ticketTypeEditor}
        onCancel={closeTicketTypeEditor}
        onOk={saveTicketType}
        confirmLoading={savingTicketType}
        okButtonProps={{
          disabled:
            !ticketTypeEditor ||
            !ticketTypeEditor.name.trim() ||
            !ticketTypeEditor.qbItem,
        }}
        destroyOnClose
      >
        {ticketTypeEditor ? (
          <Space direction="vertical" style={{ width: "100%" }}>
            <Text>Display name for this ticket type</Text>
            <Input
              placeholder="e.g. General Admission, VIP"
              value={ticketTypeEditor.name}
              onChange={(e) =>
                setTicketTypeEditor((prev) => ({ ...prev, name: e.target.value }))
              }
              autoFocus
            />
            <Text style={{ display: "block", marginTop: "0.5rem" }}>
              QuickBooks Item
            </Text>
            {ticketTypeEditor.qbItem ? (
              <Text strong>{ticketTypeEditor.qbItem.name}</Text>
            ) : (
              <Text type="secondary">None selected</Text>
            )}
            <Button onClick={() => setQbPickerOpen(true)}>
              Choose QuickBooks Item
            </Button>
          </Space>
        ) : null}
      </Modal>

      <ItemPickerModal
        open={qbPickerOpen}
        title="Choose QuickBooks Item"
        loading={qbItemsLoading}
        items={qbItems}
        onSelect={selectQbItemForTicketType}
        onCancel={() => setQbPickerOpen(false)}
      />

      <Modal
        title={imageEditorEvent ? `${imageEditorEvent.name}: Event Image` : ""}
        visible={!!imageEditorEvent}
        onCancel={closeImageEditor}
        onOk={saveImage}
        confirmLoading={savingImage}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <div>
            <Text>Preset image</Text>
            <Select
              style={{ width: "100%", marginTop: "0.25rem" }}
              placeholder="Use automatic fallback"
              value={imageMode || undefined}
              onChange={selectImagePreset}
              allowClear
              onClear={() => {
                setImageMode("");
                setImageUrlDraft("");
              }}
            >
              {eventImagePresets.map((preset) => (
                <Option key={preset.value} value={preset.value}>
                  {preset.label}
                </Option>
              ))}
              <Option value={CUSTOM_IMAGE_VALUE}>Custom image URL</Option>
            </Select>
          </div>

          {imageMode === CUSTOM_IMAGE_VALUE ? (
            <div>
              <Text>Custom image URL</Text>
              <Input
                placeholder="https://example.com/image.jpg"
                value={imageUrlDraft}
                onChange={(e) => setImageUrlDraft(e.target.value)}
                style={{ marginTop: "0.25rem" }}
                autoFocus
              />
            </div>
          ) : null}

          <Text type="secondary">
            Leave blank to use the automatic fallback image for this event.
          </Text>
        </Space>
        {(imageUrlDraft || imageEditorEvent) ? (
          <div style={{ marginTop: "1rem" }}>
            <img
              src={imageUrlDraft || getEventFallbackImage(imageEditorEvent)}
              alt="preview"
              style={{
                width: "100%",
                maxHeight: 220,
                objectFit: "cover",
                borderRadius: 4,
              }}
            />
          </div>
        ) : null}
      </Modal>

      <Modal
        title={
          scheduleEditorEvent ? `${scheduleEditorEvent.name}: Sale Window` : ""
        }
        visible={!!scheduleEditorEvent}
        onCancel={closeScheduleEditor}
        onOk={saveSchedule}
        confirmLoading={savingSchedule}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <div>
            <Text>Start date (leave blank to open immediately)</Text>
            <DatePicker
              style={{ width: "100%", marginTop: "0.25rem" }}
              value={scheduleStart}
              onChange={setScheduleStart}
              showTime
              format="MMM D, YYYY h:mm A"
            />
          </div>
          <div>
            <Text>End date (leave blank to stay open indefinitely)</Text>
            <DatePicker
              style={{ width: "100%", marginTop: "0.25rem" }}
              value={scheduleEnd}
              onChange={setScheduleEnd}
              showTime
              format="MMM D, YYYY h:mm A"
            />
          </div>
        </Space>
      </Modal>
    </div>
  );
}
